import React from 'react';
import ReactDOM from 'react-dom';
import { useLang } from '../../contexts/LanguageContext';

interface LogoutOverlayProps {
  visible: boolean;
}

export const LogoutOverlay: React.FC<LogoutOverlayProps> = ({ visible }) => {
  const { t, dir } = useLang();
  if (!visible) return null;

  const content = (
    <>
      <style>{`
        @keyframes logout-fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes logout-scale-in {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        
        @keyframes logout-spinner-rotate {
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes logout-pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }
        
        @keyframes logout-shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        
        @keyframes logout-ripple {
          0% {
            transform: scale(0.8);
            opacity: 1;
          }
          100% {
            transform: scale(2);
            opacity: 0;
          }
        }
        
        .logout-overlay {
          position: fixed;
          inset: 0;
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: logout-fade-in 0.3s ease-out;
        }
        
        .logout-overlay__backdrop {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.6) 100%);
          backdrop-filter: blur(4px);
          animation: logout-fade-in 0.3s ease-out;
        }
        
        .logout-overlay__content {
          position: relative;
          padding: 48px 40px;
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          border-radius: 24px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          max-width: 400px;
          width: 90%;
          text-align: center;
          animation: logout-scale-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          overflow: hidden;
        }
        
        .logout-overlay__content::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899, #3b82f6);
          background-size: 200% 100%;
          animation: logout-shimmer 2s linear infinite;
        }
        
        .logout-overlay__spinner-container {
          position: relative;
          width: 64px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .logout-overlay__spinner-ripple {
          position: absolute;
          width: 64px;
          height: 64px;
          border-radius: 50%;
          border: 2px solid rgba(59, 130, 246, 0.3);
          animation: logout-ripple 2s ease-out infinite;
        }
        
        .logout-overlay__spinner-ripple:nth-child(2) {
          animation-delay: 0.5s;
        }
        
        .logout-overlay__spinner {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          border: 4px solid rgba(59, 130, 246, 0.1);
          border-top-color: #3b82f6;
          border-right-color: #8b5cf6;
          animation: logout-spinner-rotate 1s linear infinite;
          position: relative;
          z-index: 1;
        }
        
        .logout-overlay__spinner-inner {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          animation: logout-pulse 1.5s ease-in-out infinite;
        }
        
        .logout-overlay__text {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          letter-spacing: 0.3px;
          animation: logout-fade-in 0.5s ease-out 0.2s both;
          direction: rtl;
          unicode-bidi: isolate;
        }
        
        .logout-overlay__dots {
          display: flex;
          gap: 6px;
          margin-top: 8px;
        }
        
        .logout-overlay__dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          animation: logout-pulse 1.4s ease-in-out infinite;
        }
        
        .logout-overlay__dot:nth-child(1) {
          animation-delay: 0s;
        }
        
        .logout-overlay__dot:nth-child(2) {
          animation-delay: 0.2s;
        }
        
        .logout-overlay__dot:nth-child(3) {
          animation-delay: 0.4s;
        }
      `}</style>
      <div className="logout-overlay">
        <div className="logout-overlay__backdrop" />
        <div className="logout-overlay__content">
          <div className="logout-overlay__spinner-container">
            <div className="logout-overlay__spinner-ripple"></div>
            <div className="logout-overlay__spinner-ripple"></div>
            <div className="logout-overlay__spinner">
              <div className="logout-overlay__spinner-inner"></div>
            </div>
          </div>
          <div>
            <div className="logout-overlay__text" dir={dir}>
              {t('auth.logoutOverlayMessage')}
            </div>
            <div className="logout-overlay__dots">
              <div className="logout-overlay__dot"></div>
              <div className="logout-overlay__dot"></div>
              <div className="logout-overlay__dot"></div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const container = document.getElementById('logout-overlay-root') || document.body;
  return ReactDOM.createPortal(content, container);
};

